import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Calendar, Eye, Home, Edit, Upload, X, QrCode, Camera, Sparkles, Send, Scale, TrendingUp, TrendingDown, Scissors, Bug, AlertTriangle, ChevronDown, ChevronUp, Download, FileDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Toggle } from '../components/ui/toggle';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { format } from 'date-fns';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, Area, ComposedChart } from 'recharts';
import QRCodeGenerator from '../components/QRCodeGenerator';
import EditHiveDialog from '../components/EditHiveDialog';
import SplitWizard from '../components/dialogs/SplitWizard';
import MiteCountDialog from '../components/MiteCountDialog';
import HiveTimeline from '../components/HiveTimeline';
import HiveOpenTasks from '../components/hive/HiveOpenTasks';
import HiveStackHistory from '../components/hive/HiveStackHistory';
import SplitProgressTracker from '../components/hive/SplitProgressTracker';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatTreatmentName = (name) => {
  if (!name) return '';
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const PhotoGalleryThumb = ({ photo, onClick }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const t = localStorage.getItem('token') || localStorage.getItem('crewToken');
    if (photo.path?.includes('/')) {
      axios.get(`${API}/files/${photo.path}`, { headers: { Authorization: `Bearer ${t}` }, responseType: 'blob' })
        .then(r => { if (!cancelled) setSrc(URL.createObjectURL(r.data)); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [photo.path]);
  if (!src) return <div className="w-24 h-24 rounded-xl bg-stone-100 animate-pulse flex-shrink-0" />;
  return (
    <button onClick={onClick} className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0 border-2 border-stone-200 hover:border-amber-400 transition-colors">
      <img src={src} alt="Inspection" className="w-full h-full object-cover" />
    </button>
  );
};

const LightboxImage = ({ path }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    const t = localStorage.getItem('token') || localStorage.getItem('crewToken');
    if (path?.includes('/')) {
      axios.get(`${API}/files/${path}`, { headers: { Authorization: `Bearer ${t}` }, responseType: 'blob' })
        .then(r => { if (!cancelled) setSrc(URL.createObjectURL(r.data)); })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [path]);
  if (!src) return <div className="w-full h-96 bg-stone-800 animate-pulse rounded-xl" />;
  return <img src={src} alt="Full size" className="w-full max-h-[80vh] object-contain rounded-xl" />;
};

const HiveDetail = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const hiveId = params.hiveId || searchParams.get('hiveId');
  const navigate = useNavigate();
  const { token, isCrew } = useAuth();
  const [hive, setHive] = useState(null);
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [yards, setYards] = useState([]);
  const [allHives, setAllHives] = useState([]);
  
  // AI Analysis state
  const [showAiDialog, setShowAiDialog] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiFollowUp, setAiFollowUp] = useState('');
  const [aiConversation, setAiConversation] = useState([]);
  
  // Weight tracking state
  const [weightHistory, setWeightHistory] = useState([]);
  const [weightSettings, setWeightSettings] = useState({ unit_preference: 'kg' });
  const [latestHeft, setLatestHeft] = useState('');
  
  // Split wizard state
  const [showSplitWizard, setShowSplitWizard] = useState(false);
  
  // Quick Mite Count state
  const [showMiteCountDialog, setShowMiteCountDialog] = useState(false);

  // Photo upload state
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  
  // Mite count history state
  const [miteCountHistory, setMiteCountHistory] = useState([]);
  
  // Treatment history state
  const [treatmentHistory, setTreatmentHistory] = useState([]);
  
  // Section expanded states (default open)
  const [weightExpanded, setWeightExpanded] = useState(true);
  const [miteExpanded, setMiteExpanded] = useState(true);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [heroBgUrl, setHeroBgUrl] = useState(null);
  const [queenInfoExpanded, setQueenInfoExpanded] = useState(false);
  const [offlineCopy, setOfflineCopy] = useState(false);

  useEffect(() => {
    fetchData();
  }, [hiveId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [hiveRes, inspectionsRes, yardsRes, hivesRes, weightHistoryRes, weightSettingsRes, miteCountsRes, treatmentsRes] = await Promise.all([
        axios.get(`${API}/hives/${hiveId}`, { headers }),
        axios.get(`${API}/inspections?hive_id=${hiveId}`, { headers }),
        axios.get(`${API}/yards`, { headers }),
        axios.get(`${API}/hives`, { headers }),
        axios.get(`${API}/hives/${hiveId}/weight-history?limit=10`, { headers }).catch(() => ({ data: { weight_history: [] } })),
        axios.get(`${API}/weight-settings`, { headers }).catch(() => ({ data: { unit_preference: 'kg' } })),
        axios.get(`${API}/mite-counts?hive_id=${hiveId}`, { headers }).catch(() => ({ data: { counts: [] } })),
        axios.get(`${API}/treatment-plans?hive_id=${hiveId}`, { headers }).catch(() => ({ data: { plans: [] } })),
      ]);
      setHive(hiveRes.data);
      setOfflineCopy(!!hiveRes.offline);
      setInspections(inspectionsRes.data);
      setYards(yardsRes.data);
      setAllHives(hivesRes.data);
      setWeightHistory(weightHistoryRes.data.weight_history || []);
      setWeightSettings(weightSettingsRes.data);
      // Get latest heft weight from latest-inspections summary
      try {
        const summaryRes = await axios.get(`${API}/hives/latest-inspections`, { headers });
        const summary = summaryRes.data?.[hiveId] || {};
        setLatestHeft(summary.heft_weight || '');
      } catch { /* ignore */ }
      setMiteCountHistory(miteCountsRes.data.counts || miteCountsRes.data || []);
      setTreatmentHistory(treatmentsRes.data.plans || treatmentsRes.data || []);
      // Fetch hero background image if stored in Object Storage
      if (hiveRes.data.hive_image && hiveRes.data.hive_image.includes('/')) {
        try {
          const imgRes = await axios.get(`${API}/files/${hiveRes.data.hive_image}`, {
            headers, responseType: 'blob'
          });
          setHeroBgUrl(URL.createObjectURL(imgRes.data));
        } catch {}
      }
    } catch (error) {
      toast.error('Failed to load hive details');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateHive = async (updatedData) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/hives/${hiveId}`, updatedData, { headers });
      toast.success('Hive updated successfully');
      fetchData();
      setShowEditDialog(false);
    } catch (error) {
      toast.error('Failed to update hive');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const authToken = localStorage.getItem('token');
      await axios.post(`${API}/hives/${hiveId}/photo`, formData, {
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Hive photo updated');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  const handleExportPDF = async () => {
    setExportingPDF(true);
    try {
      const el = document.getElementById('hive-overview-content');
      if (!el) return;
      const canvas = await html2canvas(el, {
        scale: 2, useCORS: true, logging: false, backgroundColor: '#FAFAF9',
        windowWidth: 900, scrollY: -window.scrollY,
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageW = 210;
      const pageH = 297;
      const mX = 8;
      const mY = 8;
      const printW = pageW - mX * 2;
      const imgH = (canvas.height * printW) / canvas.width;
      const printH = pageH - mY * 2;

      if (imgH <= printH) {
        pdf.addImage(imgData, 'JPEG', mX, mY, printW, imgH);
      } else {
        let yOffset = 0;
        const scale = printW / canvas.width;
        while (yOffset < imgH) {
          if (yOffset > 0) pdf.addPage();
          const sliceH = Math.min(printH, imgH - yOffset);
          const slicePx = sliceH / scale;
          const srcY = yOffset / scale;
          const sliceCanvas = document.createElement('canvas');
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = Math.ceil(slicePx);
          const ctx = sliceCanvas.getContext('2d');
          ctx.fillStyle = '#FAFAF9';
          ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
          ctx.drawImage(canvas, 0, srcY, canvas.width, slicePx, 0, 0, canvas.width, slicePx);
          pdf.addImage(sliceCanvas.toDataURL('image/jpeg', 0.92), 'JPEG', mX, mY, printW, sliceH);
          yOffset += sliceH;
        }
      }
      pdf.save(`Hive-${hive.hive_number}-Overview-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
      toast.success('PDF exported');
    } catch (err) {
      toast.error('Failed to export PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  // Group inspections by month
  const inspectionsByMonth = useMemo(() => {
    const groups = {};
    inspections.forEach(inspection => {
      const date = new Date(inspection.date);
      const monthKey = format(date, 'MMMM yyyy');
      if (!groups[monthKey]) {
        groups[monthKey] = [];
      }
      groups[monthKey].push(inspection);
    });
    return groups;
  }, [inspections]);

  // Initialize expanded months - most recent month expanded by default
  useEffect(() => {
    const monthKeys = Object.keys(inspectionsByMonth);
    if (monthKeys.length > 0 && Object.keys(expandedMonths).length === 0) {
      setExpandedMonths({ [monthKeys[0]]: true });
    }
  }, [inspectionsByMonth]);

  const toggleMonth = (monthKey) => {
    setExpandedMonths(prev => ({
      ...prev,
      [monthKey]: !prev[monthKey]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center">
        <div className="text-stone-600 text-lg">Loading...</div>
      </div>
    );
  }

  if (!hive) {
    return (
      <div className="flex flex-col items-center justify-center p-4">
        <p className="text-stone-600 text-lg mb-4">Hive not found</p>
        <Button onClick={() => navigate('/')} data-testid="back-to-dashboard-btn">
          Back to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="pb-24 honeycomb-bg">
      <div id="hive-overview-content">
      {offlineCopy && (
        <div className="bg-amber-100 text-amber-900 text-sm px-4 py-2 text-left" data-testid="hive-offline-copy-banner">
          No signal: showing the copy saved on this phone
        </div>
      )}
      {/* Compact Header with Thumbnail */}
      <div className="bg-white border-b border-stone-200 p-4 md:p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-4">
            {/* Thumbnail — tap to snap/upload */}
            <label className="w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden border-2 border-amber-300 flex-shrink-0 bg-stone-100 cursor-pointer relative group">
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoUpload}
                className="hidden"
                data-testid="hive-photo-input"
              />
              {(heroBgUrl || hive.hive_image) ? (
                <img
                  src={heroBgUrl || `${API}/uploads/${hive.hive_image}`}
                  alt={`Hive ${hive.hive_number}`}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=200&h=200&fit=crop&crop=center'; }}
                />
              ) : (
                <img
                  src="https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=200&h=200&fit=crop&crop=center"
                  alt="Default hive"
                  className="w-full h-full object-cover"
                />
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                {uploadingPhoto ? (
                  <div className="animate-spin h-6 w-6 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <Camera className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                )}
              </div>
            </label>
            {/* Hive Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl md:text-3xl font-extrabold text-stone-800 font-heading" data-testid="hive-title">
                  Hive {hive.hive_number}
                </h1>
                {hive.nickname && <span className="text-amber-600 font-bold text-lg">"{hive.nickname}"</span>}
              </div>
              {hive.yard && <p className="text-stone-600 mt-0.5">{hive.yard}</p>}
              {(() => {
                const yd = yards.find(y => y.name === hive.yard || y.name?.trim() === hive.yard);
                return yd?.location ? <p className="text-stone-400 text-sm">{yd.location}</p> : null;
              })()}
            </div>
            {/* Action Buttons */}
            <div className="flex gap-2 flex-shrink-0">
              {inspections.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleExportPDF}
                  disabled={exportingPDF}
                  className="h-10 w-10"
                  title="Download Hive Overview PDF"
                  data-testid="download-pdf-btn"
                >
                  {exportingPDF ? <div className="animate-spin h-4 w-4 border-2 border-stone-500 border-t-transparent rounded-full" /> : <Download className="h-5 w-5" />}
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setShowQRDialog(true)}
                className="h-10 w-10"
                title="QR Code"
                data-testid="qr-code-btn"
              >
                <QrCode className="h-5 w-5" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => navigate('/?view=hives')}
                className="h-10 w-10"
                title="Back to Hives"
                data-testid="back-to-hives-btn"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-6">
        <div className="flex gap-3">
          <Button
            onClick={() => navigate(`/?view=new-inspection&hiveId=${hiveId}`)}
            data-testid="new-inspection-btn"
            className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-full h-14 text-lg font-bold shadow-md hover:shadow-lg transition-all active:scale-95"
          >
            <Plus className="mr-2 h-5 w-5" />
            New Inspection
          </Button>
          <Button
            onClick={() => setShowMiteCountDialog(true)}
            data-testid="quick-mite-count-btn"
            variant="outline"
            className="h-14 px-4 rounded-full border-2 border-red-300 text-red-700 hover:bg-red-50 font-bold shadow-sm hover:shadow-md transition-all"
            title="Quick Mite Count"
          >
            <Bug className="h-5 w-5 mr-2" />
            <span className="hidden sm:inline">Mite Count</span>
          </Button>
        </div>

        <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-stone-800 font-heading">Hive Information</h2>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowSplitWizard(true)}
                data-testid="split-hive-btn"
                className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
              >
                <Scissors className="mr-2 h-4 w-4" />
                Split
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowEditDialog(true)}
                data-testid="edit-hive-btn"
                className="rounded-full"
              >
                <Edit className="mr-2 h-4 w-4" />
                Edit Hive
              </Button>
            </div>
          </div>
          <div className="space-y-3 text-stone-600">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="font-medium text-stone-800 block text-sm">Apiary</span>
                <p className="text-stone-700">{hive.yard || 'Not assigned'}</p>
              </div>
              {(() => {
                const yardData = yards.find(y => y.name === hive.yard || y.name?.trim() === hive.yard);
                const loc = yardData?.location || hive.location;
                return loc ? (
                  <div>
                    <span className="font-medium text-stone-800 block text-sm">Apiary Location</span>
                    <p className="text-stone-700" data-testid="apiary-location">{loc}</p>
                  </div>
                ) : null;
              })()}
            </div>
            
            {/* Status */}
            <div>
              <span className="font-medium text-stone-800 block text-sm">Status</span>
              <span className={`capitalize font-medium ${hive.status === 'inactive' ? 'text-red-600' : 'text-green-600'}`}>
                {hive.status === 'inactive' ? '⚠️ ' : '✓ '}{hive.status}
              </span>
              {hive.status === 'inactive' && hive.inactive_reason && (
                <p className="text-red-600 text-sm mt-1">Reason: {hive.inactive_reason}</p>
              )}
            </div>
            {/* AFB Critical Alert Banner */}
            {hive.status === 'inactive' && (hive.inactive_reason || '').toLowerCase().includes('afb') && (
              <div className="col-span-2 bg-red-600 text-white rounded-xl p-4">
                <div className="font-bold text-lg mb-1">🔥 AFB DETECTED — ACTION REQUIRED</div>
                <ul className="text-sm space-y-1 list-disc list-inside">
                  <li>ALL frames, boxes & equipment from this hive <strong>must be burned</strong></li>
                  <li>Report to your local apiary inspector / biosecurity authority</li>
                  <li>Inspect all neighbouring hives for signs of AFB</li>
                  <li>Sterilise all tools that contacted this hive</li>
                </ul>
              </div>
            )}
            {hive.last_inspection && (
              <div>
                <span className="font-medium text-stone-800 block text-sm">Last Inspection</span>
                <p className="text-stone-700">{format(new Date(hive.last_inspection), 'PPP')}</p>
              </div>
            )}

            {/* Hive Stack Configuration */}
            {hive.hive_stack && Object.keys(hive.hive_stack).length > 0 && (
              <div>
                <span className="font-medium text-stone-800 block text-sm mb-2">Hive Stack Configuration</span>
                <div className="bg-stone-50 rounded-xl p-4 space-y-2 text-sm">
                  {/* Hive Type */}
                  {hive.hive_stack.hive_type && (
                    <div className="flex justify-between border-b border-stone-200 pb-2 mb-2">
                      <span className="font-semibold text-amber-700">Type:</span>
                      <span className="font-bold text-amber-800 capitalize">{hive.hive_stack.hive_type.replace('_', ' ')}</span>
                    </div>
                  )}
                  
                  {/* Dynamic Box Configuration (new system) */}
                  {(hive.hive_stack.box_config || hive.box_config) && Object.keys(hive.hive_stack.box_config || hive.box_config || {}).length > 0 && (
                    <div className="space-y-2">
                      {Object.entries(hive.hive_stack.box_config || hive.box_config || {}).map(([boxName, config]) => {
                        if (!config || config.qty === 0) return null;
                        return (
                          <div key={boxName} className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                            <div className="flex justify-between items-center">
                              <span className="font-medium text-amber-800">{boxName}:</span>
                              <span className="font-semibold text-stone-800">{config.qty}{config.size ? ` x ${config.size}F` : ''}</span>
                            </div>
                            {(config.frame_type || config.foundation_type) && (
                              <div className="flex gap-4 mt-1 text-xs text-stone-600">
                                {config.frame_type && (
                                  <span>Frame: <span className="font-medium">{config.frame_type}</span></span>
                                )}
                                {config.foundation_type && (
                                  <span>Foundation: <span className="font-medium">{config.foundation_type}</span></span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {/* Legacy Langstroth (if no box_config) */}
                  {!hive.hive_stack.box_config && hive.hive_stack.deep_brood_qty > 0 && (
                    <div className="flex justify-between">
                      <span>Deep Brood:</span>
                      <span className="font-semibold">{hive.hive_stack.deep_brood_qty} x {hive.hive_stack.deep_brood_size}F</span>
                    </div>
                  )}
                  {!hive.hive_stack.box_config && hive.hive_stack.deep_super_qty > 0 && (
                    <div className="flex justify-between">
                      <span>Deep Super:</span>
                      <span className="font-semibold">{hive.hive_stack.deep_super_qty} x {hive.hive_stack.deep_super_size}F</span>
                    </div>
                  )}
                  {!hive.hive_stack.box_config && hive.hive_stack.ideal_super_qty > 0 && (
                    <div className="flex justify-between">
                      <span>Ideal Super:</span>
                      <span className="font-semibold">{hive.hive_stack.ideal_super_qty} x {hive.hive_stack.ideal_super_size}F</span>
                    </div>
                  )}
                  
                  {/* Top Bar Hive */}
                  {hive.hive_stack.top_hive_frames && (
                    <div className="flex justify-between">
                      <span>Frames:</span>
                      <span className="font-semibold">{hive.hive_stack.top_hive_frames}</span>
                    </div>
                  )}
                  
                  {/* Horizontal Hive */}
                  {hive.hive_stack.horizontal_hive_frames && (
                    <div className="flex justify-between">
                      <span>Frames:</span>
                      <span className="font-semibold">{hive.hive_stack.horizontal_hive_frames}</span>
                    </div>
                  )}
                  {hive.hive_stack.horizontal_hive_entrances && (
                    <div className="flex justify-between">
                      <span>Entrances:</span>
                      <span className="font-semibold">{hive.hive_stack.horizontal_hive_entrances}</span>
                    </div>
                  )}
                  {hive.hive_stack.horizontal_hive_colonies && (
                    <div className="flex justify-between">
                      <span>Colonies:</span>
                      <span className="font-semibold">{hive.hive_stack.horizontal_hive_colonies}</span>
                    </div>
                  )}
                  
                  {/* Layens Hive */}
                  {hive.hive_stack.layens_hive_frames && (
                    <div className="flex justify-between">
                      <span>Frames:</span>
                      <span className="font-semibold">{hive.hive_stack.layens_hive_frames}</span>
                    </div>
                  )}
                  
                  {/* NUC */}
                  {hive.hive_stack.nuc_frames && (
                    <div className="flex justify-between">
                      <span>Frames:</span>
                      <span className="font-semibold">{hive.hive_stack.nuc_frames}</span>
                    </div>
                  )}
                  
                  {/* Flow Hive */}
                  {hive.hive_stack.flow_hive_deep_brood_qty > 0 && (
                    <div className="flex justify-between">
                      <span>Deep Brood:</span>
                      <span className="font-semibold">{hive.hive_stack.flow_hive_deep_brood_qty} x {hive.hive_stack.flow_hive_deep_brood_size}F</span>
                    </div>
                  )}
                  {hive.hive_stack.flow_hive_super_size && (
                    <div className="flex justify-between">
                      <span>Flow Super:</span>
                      <span className="font-semibold">{hive.hive_stack.flow_hive_super_size}F</span>
                    </div>
                  )}
                  
                  {/* National */}
                  {hive.hive_stack.national_deep_qty > 0 && (
                    <div className="flex justify-between">
                      <span>Deep National {hive.hive_stack.national_variant || '1'}:</span>
                      <span className="font-semibold">{hive.hive_stack.national_deep_qty} x {hive.hive_stack.national_deep_size}F</span>
                    </div>
                  )}
                  {hive.hive_stack.national_shallow_qty > 0 && (
                    <div className="flex justify-between">
                      <span>Shallow National {hive.hive_stack.national_variant || '1'}:</span>
                      <span className="font-semibold">{hive.hive_stack.national_shallow_qty} x {hive.hive_stack.national_shallow_size}F</span>
                    </div>
                  )}
                  
                  {/* Keepers Hive */}
                  {hive.hive_stack.keepers_type && (
                    <>
                      <div className="flex justify-between">
                        <span>Keeper Type:</span>
                        <span className="font-semibold capitalize">{hive.hive_stack.keepers_type.replace('_', ' ')}</span>
                      </div>
                      {hive.hive_stack.keepers_medium_nuc_qty > 0 && (
                        <div className="flex justify-between">
                          <span>5F Medium NUC:</span>
                          <span className="font-semibold">{hive.hive_stack.keepers_medium_nuc_qty} boxes</span>
                        </div>
                      )}
                      {hive.hive_stack.keepers_langstroth_deep_qty > 0 && (
                        <div className="flex justify-between">
                          <span>8F Langstroth Deep:</span>
                          <span className="font-semibold">{hive.hive_stack.keepers_langstroth_deep_qty} boxes</span>
                        </div>
                      )}
                      {hive.hive_stack.keepers_super_deep_qty > 0 && (
                        <div className="flex justify-between">
                          <span>8F Super Deep:</span>
                          <span className="font-semibold">{hive.hive_stack.keepers_super_deep_qty} boxes</span>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Warre Hive */}
                  {hive.hive_stack.warre_boxes > 0 && (
                    <div className="flex justify-between">
                      <span>Boxes (8F):</span>
                      <span className="font-semibold">{hive.hive_stack.warre_boxes}</span>
                    </div>
                  )}
                  
                  {/* Queen Mating Box */}
                  {hive.hive_stack.queen_mating_frames && (
                    <div className="flex justify-between">
                      <span>Frames:</span>
                      <span className="font-semibold">{hive.hive_stack.queen_mating_frames}</span>
                    </div>
                  )}
                  
                  {/* Other */}
                  {hive.hive_stack.other_description && (
                    <div className="flex justify-between">
                      <span>Description:</span>
                      <span className="font-semibold">{hive.hive_stack.other_description}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            {/* Live Equipment Config (updated via inspections) */}
            {hive.notes && (
              <div>
                <span className="font-medium text-stone-800 block text-sm mb-1">Notes</span>
                <p className="text-stone-700">{hive.notes}</p>
              </div>
            )}

            {/* Split Information */}
            {hive.is_split && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                <span className="font-medium text-emerald-800 block text-sm mb-2">✂️ Split Information</span>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {(hive.origin_date || hive.created_at) && (
                    <div>
                      <span className="text-emerald-600">Date:</span>
                      <span className="font-semibold text-emerald-800 ml-2">
                        {new Date(hive.origin_date || hive.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                  )}
                  {hive.split_from_hive && (
                    <div>
                      <span className="text-emerald-600">Split from:</span>
                      <span className="font-semibold text-emerald-800 ml-2">
                        {(() => { const p = allHives.find(h => h.id === hive.split_from_hive); return p ? `Hive ${p.hive_number}` : hive.split_from_hive; })()}
                      </span>
                    </div>
                  )}
                  {hive.split_type && (
                    <div>
                      <span className="text-emerald-600">Split type:</span>
                      <span className="font-semibold text-emerald-800 ml-2">
                        {hive.split_type === 'walk-away' ? 'Walk-away Split' : hive.split_type === 'queen-right' ? 'Queen Right' : (hive.split_type_other || 'Other')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Origin / Swarm Capture Information */}
            {hive.origin_type && (
              <div className={`border rounded-xl p-3 ${hive.origin_type === 'Swarm Capture' || hive.origin_type === 'Swarm Trap' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
                <span className={`font-medium block text-sm mb-2 ${hive.origin_type === 'Swarm Capture' || hive.origin_type === 'Swarm Trap' ? 'text-amber-800' : 'text-blue-800'}`}>
                  {hive.origin_type === 'Swarm Capture' || hive.origin_type === 'Swarm Trap' ? '🐝 Swarm Capture' : `📦 Origin: ${hive.origin_type}`}
                </span>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {hive.origin_date && (
                    <div>
                      <span className="text-stone-500">{hive.origin_type === 'Swarm Capture' || hive.origin_type === 'Swarm Trap' ? 'Captured:' : hive.origin_type === 'Package' ? 'Purchased:' : hive.origin_type === 'Split' ? 'Split date:' : 'Acquired:'}</span>
                      <span className="font-semibold text-stone-800 ml-2">{new Date(hive.origin_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    </div>
                  )}
                  {hive.origin_location && (
                    <div>
                      <span className="text-stone-500">Location:</span>
                      <span className="font-semibold text-stone-800 ml-2">{hive.origin_location}</span>
                    </div>
                  )}
                  {hive.bee_variety && (
                    <div>
                      <span className="text-stone-500">Bee variety:</span>
                      <span className="font-semibold text-stone-800 ml-2">{hive.bee_variety}</span>
                    </div>
                  )}
                  {hive.notes && hive.notes.includes('Swarm captured from trap') && (
                    <div className="col-span-2">
                      <span className="text-stone-500">Trap details:</span>
                      <span className="font-semibold text-stone-800 ml-2">{hive.notes.split('.')[0]}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Queen Information — collapsible */}
            {(hive.queen_source || hive.queen_genetics || hive.queen_installed_date || (hive.queen_history && hive.queen_history.length > 0)) && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl overflow-hidden" data-testid="queen-info-section">
                <button
                  type="button"
                  onClick={() => setQueenInfoExpanded(!queenInfoExpanded)}
                  className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-purple-100 transition-colors"
                  data-testid="queen-info-toggle"
                >
                  <span className="font-medium text-purple-800 text-sm flex items-center gap-1.5">
                    👑 Queen Information
                    {hive.queen_installed_date && (
                      <span className="text-xs text-purple-500 font-normal">— installed {format(new Date(hive.queen_installed_date), 'PPP')}</span>
                    )}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-purple-500 transition-transform ${queenInfoExpanded ? 'rotate-180' : ''}`} />
                </button>
                {queenInfoExpanded && (
                <div className="px-3 pb-3 space-y-2 text-sm border-t border-purple-200 pt-2">
                  {hive.queen_source && (
                    <div className="flex justify-between"><span className="text-purple-600">Source:</span><span className="font-semibold text-purple-800">{hive.queen_source}</span></div>
                  )}
                  {hive.queen_genetics && (
                    <div className="flex justify-between"><span className="text-purple-600">Genetics:</span><span className="font-semibold text-purple-800">{hive.queen_genetics}</span></div>
                  )}
                  {hive.queen_installed_date && (
                    <div className="flex justify-between"><span className="text-purple-600">Installed:</span><span className="font-semibold text-purple-800">{format(new Date(hive.queen_installed_date), 'PPP')}</span></div>
                  )}
                  {hive.queen_history && hive.queen_history.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-purple-200">
                      <span className="text-purple-700 font-medium block mb-2">Queen History ({hive.queen_history.length}):</span>
                      <div className="space-y-2">
                        {hive.queen_history.slice().reverse().map((entry, idx) => (
                          <div key={idx} className="bg-white rounded-lg p-2 border border-purple-100">
                            <div className="flex justify-between text-xs">
                              <span className="text-purple-600">{format(new Date(entry.date), 'PPP')}</span>
                              <span className="capitalize bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{entry.event?.replace('_', ' ')}</span>
                            </div>
                            {entry.batch_name && <div className="text-xs text-stone-600 mt-1">Batch: {entry.batch_name}</div>}
                            {entry.breeder_queen && <div className="text-xs text-stone-600">Breeder: {entry.breeder_queen}</div>}
                            {entry.notes && <div className="text-xs text-stone-500 mt-1 italic">{entry.notes}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                )}
              </div>
            )}
          </div>
        </div>
        {(weightHistory.length > 0 || latestHeft) && (
          <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-hidden">
            <button
              onClick={() => setWeightExpanded(!weightExpanded)}
              className="w-full p-6 flex items-center justify-between hover:bg-stone-50 transition-colors"
              data-testid="weight-trend-toggle"
            >
              <h2 className="text-xl font-bold text-stone-800 font-heading flex items-center gap-2">
                <Scale className="h-5 w-5 text-emerald-600" />
                Weight Trend
                <span className="text-sm font-normal text-stone-500">({weightHistory.length} records{latestHeft ? ` · Heft: ${latestHeft}` : ''})</span>
              </h2>
              {weightExpanded ? (
                <ChevronUp className="h-5 w-5 text-stone-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-stone-400" />
              )}
            </button>
            {weightExpanded && (
              <div className="px-6 pb-6 space-y-4">
              {/* Heft Assessment Card */}
              {latestHeft && (
                <div className={`rounded-xl p-4 border flex items-center gap-3 ${
                  latestHeft === 'Heavy' ? 'bg-green-50 border-green-200' : latestHeft === 'Medium' ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'
                }`} data-testid="heft-weight-card">
                  <Scale className={`h-6 w-6 ${
                    latestHeft === 'Heavy' ? 'text-green-600' : latestHeft === 'Medium' ? 'text-amber-600' : 'text-red-600'
                  }`} />
                  <div>
                    <div className="text-xs text-stone-500">Heft Assessment</div>
                    <div className={`text-lg font-bold ${
                      latestHeft === 'Heavy' ? 'text-green-700' : latestHeft === 'Medium' ? 'text-amber-700' : 'text-red-700'
                    }`}>{latestHeft}</div>
                    <div className="text-[10px] text-stone-400">
                      {latestHeft === 'Heavy' ? 'Well provisioned — over 40kg / 88lbs' : latestHeft === 'Medium' ? 'Adequate stores — 25–40kg / 55–88lbs' : 'Low stores — under 25kg / 55lbs, may need feeding'}
                    </div>
                  </div>
                </div>
              )}

              {/* Mini Chart - Simple bar representation */}
              {weightHistory.length > 0 && (
              <>
              <div className="bg-gradient-to-r from-emerald-50 to-amber-50 rounded-xl p-4 border border-emerald-100">
                <div className="flex items-end gap-1 h-24 mb-2">
                  {weightHistory.slice().reverse().map((record, idx) => {
                    const weight = parseFloat(record.hive_weight);
                    const maxWeight = Math.max(...weightHistory.map(r => parseFloat(r.hive_weight)));
                    const heightPercent = maxWeight > 0 ? (weight / maxWeight) * 100 : 0;
                    const isLatest = idx === weightHistory.length - 1;
                    return (
                      <div 
                        key={record.id || idx}
                        className="flex-1 flex flex-col items-center"
                        title={`${new Date(record.date).toLocaleDateString()}: ${weight} ${weightSettings.unit_preference}`}
                      >
                        <div 
                          className={`w-full rounded-t-sm transition-all ${isLatest ? 'bg-emerald-500' : 'bg-emerald-300'}`}
                          style={{ height: `${heightPercent}%`, minHeight: '4px' }}
                        />
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{weightHistory.length > 0 ? new Date(weightHistory[weightHistory.length - 1]?.date).toLocaleDateString() : ''}</span>
                  <span>Latest</span>
                </div>
              </div>
              
              {/* Weight Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <div className="text-xs text-emerald-600 mb-1">Latest</div>
                  <div className="text-xl font-bold text-emerald-700">
                    {weightSettings.unit_preference === 'kg' 
                      ? `${parseFloat(weightHistory[0]?.hive_weight || 0).toFixed(1)} kg`
                      : `${(parseFloat(weightHistory[0]?.hive_weight || 0) * 2.20462).toFixed(1)} lbs`
                    }
                  </div>
                </div>
                <div className={`rounded-lg p-3 text-center ${
                  weightHistory.length >= 2 && parseFloat(weightHistory[0]?.hive_weight) >= parseFloat(weightHistory[1]?.hive_weight)
                    ? 'bg-green-50'
                    : 'bg-red-50'
                }`}>
                  <div className="text-xs text-gray-600 mb-1">Change</div>
                  {weightHistory.length >= 2 ? (
                    <div className={`text-xl font-bold flex items-center justify-center gap-1 ${
                      parseFloat(weightHistory[0]?.hive_weight) >= parseFloat(weightHistory[1]?.hive_weight)
                        ? 'text-green-700'
                        : 'text-red-700'
                    }`}>
                      {parseFloat(weightHistory[0]?.hive_weight) >= parseFloat(weightHistory[1]?.hive_weight)
                        ? <TrendingUp className="h-5 w-5" />
                        : <TrendingDown className="h-5 w-5" />
                      }
                      {weightSettings.unit_preference === 'kg' 
                        ? `${(parseFloat(weightHistory[0]?.hive_weight) - parseFloat(weightHistory[1]?.hive_weight)).toFixed(1)} kg`
                        : `${((parseFloat(weightHistory[0]?.hive_weight) - parseFloat(weightHistory[1]?.hive_weight)) * 2.20462).toFixed(1)} lbs`
                      }
                    </div>
                  ) : (
                    <div className="text-xl font-bold text-gray-400">--</div>
                  )}
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <div className="text-xs text-amber-600 mb-1">Records</div>
                  <div className="text-xl font-bold text-amber-700">{weightHistory.length}</div>
                </div>
              </div>
              </>
              )}
            </div>
            )}
          </div>
        )}

        {/* Mite Count Trend Section - Collapsible */}
        {miteCountHistory.length > 0 && (
          <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-hidden" data-testid="mite-trend-section">
            <button
              onClick={() => setMiteExpanded(!miteExpanded)}
              className="w-full p-6 flex items-center justify-between hover:bg-stone-50 transition-colors"
              data-testid="mite-trend-toggle"
            >
              <h2 className="text-xl font-bold text-stone-800 font-heading flex items-center gap-2">
                <Bug className="h-5 w-5 text-red-600" />
                Mite Count Trend
                <span className="text-sm font-normal text-stone-500">({miteCountHistory.length} tests)</span>
                {miteCountHistory[0]?.percentage >= 3 && (
                  <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                    Treatment Needed
                  </span>
                )}
              </h2>
              {miteExpanded ? (
                <ChevronUp className="h-5 w-5 text-stone-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-stone-400" />
              )}
            </button>
            {miteExpanded && (
            <div className="px-6 pb-6 space-y-4">
              {/* Chart with Treatment Markers */}
              <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-4 border border-red-100">
                {/* Treatment Legend */}
                {treatmentHistory.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3 text-xs">
                    <span className="text-stone-500">Treatments:</span>
                    {treatmentHistory.map((t, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                        {formatTreatmentName(t.treatment_type_name || t.name || t.treatment_type) || 'Treatment'} ({format(new Date(t.start_date), 'MMM d')})
                      </span>
                    ))}
                  </div>
                )}
                <ResponsiveContainer width="100%" height={180}>
                  <ComposedChart 
                    data={[...miteCountHistory].reverse().map(record => {
                      const recordDate = new Date(record.date);
                      // Check if this date falls within any treatment period
                      const activeTreatment = treatmentHistory.find(t => {
                        const startDate = new Date(t.start_date);
                        const endDate = t.end_date ? new Date(t.end_date) : new Date(startDate.getTime() + 7 * 24 * 60 * 60 * 1000);
                        return recordDate >= startDate && recordDate <= endDate;
                      });
                      return {
                        date: recordDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                        fullDate: record.date,
                        percentage: record.percentage || 0,
                        miteCount: record.mite_count || 0,
                        method: record.method || 'unknown',
                        treatment: activeTreatment ? formatTreatmentName(activeTreatment.treatment_type_name || activeTreatment.name || activeTreatment.treatment_type) : null
                      };
                    })}
                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient id="miteGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#fecaca" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#9ca3af" />
                    <YAxis 
                      tick={{ fontSize: 10 }} 
                      stroke="#9ca3af"
                      tickFormatter={(value) => `${value}%`}
                      domain={[0, 'auto']}
                    />
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          const isHigh = data.percentage >= 3;
                          return (
                            <div className={`p-3 rounded-lg shadow-lg border ${isHigh ? 'bg-red-50 border-red-200' : 'bg-white border-stone-200'}`}>
                              <p className="font-bold text-stone-800">{data.date}</p>
                              <p className={`text-lg font-bold ${isHigh ? 'text-red-600' : 'text-stone-700'}`}>
                                {data.percentage.toFixed(1)}% ({data.miteCount} mites)
                              </p>
                              <p className="text-xs text-stone-500 capitalize">{data.method.replace('_', ' ')}</p>
                              {data.treatment && (
                                <p className="text-xs text-purple-600 font-medium mt-1 flex items-center gap-1">
                                  <Scissors className="h-3 w-3" /> During: {data.treatment}
                                </p>
                              )}
                              {isHigh && !data.treatment && (
                                <p className="text-xs text-red-600 font-medium mt-1 flex items-center gap-1">
                                  <AlertTriangle className="h-3 w-3" /> Treatment recommended
                                </p>
                              )}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    {/* Danger zone reference line at 3% */}
                    <ReferenceLine y={3} stroke="#ef4444" strokeDasharray="5 5" label={{ value: '3% Threshold', fontSize: 10, fill: '#ef4444', position: 'right' }} />
                    <Area 
                      type="monotone" 
                      dataKey="percentage" 
                      fill="url(#miteGradient)" 
                      stroke="none"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="percentage" 
                      stroke="#ef4444" 
                      strokeWidth={2}
                      dot={{ fill: '#ef4444', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, fill: '#dc2626' }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              {/* Mite Count Summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className={`rounded-lg p-3 text-center ${
                  miteCountHistory[0]?.percentage >= 3 ? 'bg-red-100' : 'bg-green-50'
                }`}>
                  <div className={`text-xs mb-1 ${miteCountHistory[0]?.percentage >= 3 ? 'text-red-600' : 'text-green-600'}`}>Latest</div>
                  <div className={`text-xl font-bold ${miteCountHistory[0]?.percentage >= 3 ? 'text-red-700' : 'text-green-700'}`}>
                    {(miteCountHistory[0]?.percentage || 0).toFixed(1)}%
                  </div>
                  <div className="text-xs text-stone-500">
                    {miteCountHistory[0]?.mite_count || 0} mites
                  </div>
                </div>
                <div className={`rounded-lg p-3 text-center ${
                  miteCountHistory.length >= 2 && miteCountHistory[0]?.percentage <= miteCountHistory[1]?.percentage
                    ? 'bg-green-50'
                    : 'bg-red-50'
                }`}>
                  <div className="text-xs text-gray-600 mb-1">Trend</div>
                  {miteCountHistory.length >= 2 ? (
                    <div className={`text-xl font-bold flex items-center justify-center gap-1 ${
                      miteCountHistory[0]?.percentage <= miteCountHistory[1]?.percentage
                        ? 'text-green-700'
                        : 'text-red-700'
                    }`}>
                      {miteCountHistory[0]?.percentage <= miteCountHistory[1]?.percentage
                        ? <TrendingDown className="h-5 w-5" />
                        : <TrendingUp className="h-5 w-5" />
                      }
                      {Math.abs((miteCountHistory[0]?.percentage || 0) - (miteCountHistory[1]?.percentage || 0)).toFixed(1)}%
                    </div>
                  ) : (
                    <div className="text-xl font-bold text-gray-400">--</div>
                  )}
                </div>
                <div className="bg-orange-50 rounded-lg p-3 text-center">
                  <div className="text-xs text-orange-600 mb-1">Tests</div>
                  <div className="text-xl font-bold text-orange-700">{miteCountHistory.length}</div>
                </div>
              </div>

              {/* Warning if above threshold */}
              {miteCountHistory[0]?.percentage >= 3 && (
                <div className="bg-red-100 border border-red-300 rounded-lg p-3 flex items-center gap-3">
                  <AlertTriangle className="h-6 w-6 text-red-600 flex-shrink-0" />
                  <div>
                    <p className="font-bold text-red-800">Treatment Recommended</p>
                    <p className="text-sm text-red-700">Mite levels are above the 3% threshold. Consider treatment options.</p>
                  </div>
                </div>
              )}

              {/* Treatment History Section */}
              {treatmentHistory.length > 0 && (
                <div className="border-t border-stone-200 pt-4 mt-4">
                  <h3 className="text-lg font-bold text-stone-800 mb-3 flex items-center gap-2">
                    <Scissors className="h-4 w-4 text-purple-600" />
                    Treatment History
                  </h3>
                  <div className="space-y-3">
                    {treatmentHistory.map((treatment) => {
                      const preMite = treatment.pre_treatment_mite_count;
                      const postMite = treatment.post_treatment_mite_count;
                      const hasPrePost = preMite !== null && preMite !== undefined && postMite !== null && postMite !== undefined;
                      const efficacy = hasPrePost && preMite > 0
                        ? Math.round(((preMite - postMite) / preMite) * 100)
                        : null;
                      const isEffective = efficacy !== null && efficacy >= 80;
                      const statusColors = {
                        'completed': 'bg-green-100 text-green-700 border-green-200',
                        'active': 'bg-blue-100 text-blue-700 border-blue-200',
                        'scheduled': 'bg-yellow-100 text-yellow-700 border-yellow-200',
                        'cancelled': 'bg-gray-100 text-gray-700 border-gray-200'
                      };
                      
                      return (
                        <div 
                          key={treatment.id} 
                          className={`p-4 rounded-xl border ${treatment.status === 'completed' ? 'bg-purple-50 border-purple-200' : 'bg-stone-50 border-stone-200'}`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="font-bold text-stone-800">{formatTreatmentName(treatment.treatment_type_name || treatment.name || treatment.treatment_type) || 'Treatment Plan'}</div>
                              <div className="text-xs text-stone-500">
                                {treatment.start_date && format(new Date(treatment.start_date), 'MMM d, yyyy')}
                                {treatment.end_date && ` - ${format(new Date(treatment.end_date), 'MMM d, yyyy')}`}
                              </div>
                            </div>
                            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${statusColors[treatment.status] || statusColors.scheduled}`}>
                              {treatment.status ? treatment.status.charAt(0).toUpperCase() + treatment.status.slice(1) : 'Scheduled'}
                            </span>
                          </div>
                          
                          {/* Mite Count Before/After */}
                          {hasPrePost && (
                            <div className="flex items-center gap-4 mt-3 p-3 bg-white rounded-lg border border-stone-100">
                              <div className="text-center">
                                <div className="text-xs text-stone-500">Before</div>
                                <div className={`text-lg font-bold ${preMite >= 3 ? 'text-red-600' : 'text-stone-700'}`}>
                                  {preMite.toFixed(1)}%
                                </div>
                              </div>
                              <div className="flex-1 flex items-center justify-center">
                                <div className="h-0.5 flex-1 bg-stone-200"></div>
                                <div className={`mx-2 px-2 py-1 rounded-full text-xs font-bold ${
                                  efficacy !== null 
                                    ? isEffective 
                                      ? 'bg-green-100 text-green-700' 
                                      : 'bg-orange-100 text-orange-700'
                                    : 'bg-stone-100 text-stone-500'
                                }`}>
                                  {efficacy !== null ? (
                                    <>
                                      {isEffective ? <TrendingDown className="h-3 w-3 inline mr-1" /> : <TrendingUp className="h-3 w-3 inline mr-1" />}
                                      {efficacy}% reduction
                                    </>
                                  ) : 'Pending'}
                                </div>
                                <div className="h-0.5 flex-1 bg-stone-200"></div>
                              </div>
                              <div className="text-center">
                                <div className="text-xs text-stone-500">After</div>
                                <div className={`text-lg font-bold ${postMite >= 3 ? 'text-red-600' : 'text-green-600'}`}>
                                  {postMite.toFixed(1)}%
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {treatment.notes && (
                            <p className="text-sm text-stone-600 mt-2 italic">"{treatment.notes}"</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No treatments message */}
              {treatmentHistory.length === 0 && miteCountHistory.length > 0 && (
                <div className="border-t border-stone-200 pt-4 mt-4">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                    <Scissors className="h-8 w-8 text-purple-400 mx-auto mb-2" />
                    <p className="text-purple-700 font-medium">No Treatment History</p>
                    <p className="text-purple-600 text-sm">Start a treatment plan from the inspection form to track efficacy</p>
                  </div>
                </div>
              )}
            </div>
            )}
          </div>
        )}

        <SplitProgressTracker hiveId={hiveId} />

        <HiveOpenTasks hiveId={hiveId} />

        <HiveStackHistory hiveId={hiveId} />

        {/* Hive Timeline */}
        <HiveTimeline hiveId={hiveId} />

        {/* Recent Photo Gallery */}
        {(() => {
          const allPhotos = inspections
            .filter(i => i.photos && i.photos.length > 0)
            .flatMap(i => i.photos.map(p => ({ path: p, date: i.date, inspectionId: i.id })))
            .slice(0, 5);
          if (allPhotos.length === 0) return null;
          return (
            <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-bold text-stone-800 font-heading mb-4 flex items-center gap-2">
                <Camera className="h-5 w-5 text-amber-600" />
                Recent Photos ({allPhotos.length})
              </h2>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {allPhotos.map((photo, idx) => (
                  <PhotoGalleryThumb key={idx} photo={photo} onClick={() => setLightboxPhoto(photo)} />
                ))}
              </div>
            </div>
          );
        })()}

        {/* Lightbox */}
        {lightboxPhoto && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setLightboxPhoto(null)}>
            <div className="relative max-w-3xl max-h-[85vh] w-full" onClick={e => e.stopPropagation()}>
              <button onClick={() => setLightboxPhoto(null)} className="absolute -top-10 right-0 text-white hover:text-amber-300 transition-colors">
                <X className="h-8 w-8" />
              </button>
              <LightboxImage path={lightboxPhoto.path} />
              <p className="text-white/70 text-center text-sm mt-2">
                {lightboxPhoto.date ? format(new Date(lightboxPhoto.date), 'PPP') : ''}
              </p>
            </div>
          </div>
        )}

        {/* Inspection History - Grouped by Month */}
        <div className="bg-white border border-stone-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-xl font-bold text-stone-800 font-heading mb-4">
            Inspection History ({inspections.length})
          </h2>
          {inspections.length === 0 ? (
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-8 text-center">
              <Calendar className="h-12 w-12 text-stone-400 mx-auto mb-3" />
              <p className="text-stone-500">No inspections yet</p>
              <p className="text-stone-400 text-sm mt-1">Start your first inspection to track this hive</p>
            </div>
          ) : (
            <div className="space-y-3">
              {Object.entries(inspectionsByMonth).map(([monthKey, monthInspections]) => (
                <div key={monthKey} className="border border-stone-200 rounded-xl overflow-hidden">
                  {/* Month Header - Collapsible */}
                  <button
                    onClick={() => toggleMonth(monthKey)}
                    className="w-full px-4 py-3 bg-stone-50 hover:bg-stone-100 flex items-center justify-between transition-colors"
                    data-testid={`month-toggle-${monthKey.replace(/\s+/g, '-')}`}
                  >
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-amber-600" />
                      <span className="font-semibold text-stone-800">{monthKey}</span>
                      <span className="text-sm text-stone-500">({monthInspections.length} inspection{monthInspections.length !== 1 ? 's' : ''})</span>
                    </div>
                    {expandedMonths[monthKey] ? (
                      <ChevronUp className="h-4 w-4 text-stone-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-stone-400" />
                    )}
                  </button>
                  
                  {/* Month's Inspections */}
                  {expandedMonths[monthKey] && (
                    <div className="p-3 space-y-2">
                      {monthInspections.map((inspection) => (
                        <div
                          key={inspection.id}
                          onClick={() => navigate(`/?view=inspection&inspectionId=${inspection.id}`)}
                          data-testid={`inspection-${inspection.id}`}
                          className="bg-white border border-stone-200 rounded-lg p-4 hover:border-amber-300 hover:bg-amber-50/50 transition-all cursor-pointer active:scale-[0.99]"
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <div className="text-lg font-bold text-stone-800 font-mono">
                                {format(new Date(inspection.date), 'PPP')}
                              </div>
                              {inspection.beekeeper && (
                                <div className="text-stone-600 text-sm mt-1">By {inspection.beekeeper}</div>
                              )}
                              {inspection.population && (
                                <div className="text-stone-500 text-sm mt-2">
                                  Population: <span className="capitalize">{inspection.population}</span>
                                </div>
                              )}
                              {inspection.queen_spotted && (
                                <div className="text-lime-600 font-medium text-sm mt-1">✓ Queen Spotted</div>
                              )}
                              {inspection.no_queen && (
                                <div className="text-red-600 font-medium text-sm mt-1">❌ No Queen</div>
                              )}
                              {inspection.queen_cells && (
                                <div className="text-amber-600 font-medium text-sm mt-1">👑 Queen Cells Present</div>
                              )}
                              {inspection.colony_mood === 'Defensive' && (
                                <div className="text-orange-600 font-medium text-sm mt-1">😤 Defensive Colony</div>
                              )}
                              {inspection.foul_odour && (
                                <div className="text-red-600 font-medium text-sm mt-1">🦨 Foul Odour Detected</div>
                              )}
                              {inspection.drone_layer && (
                                <div className="text-orange-600 font-medium text-sm mt-1">🐝 Drone Layer</div>
                              )}
                              {(inspection.hive_condition === 'Damaged' || inspection.hive_condition === 'Poor') && (
                                <div className="text-red-600 font-medium text-sm mt-1">⚠️ Hive Condition: {inspection.hive_condition}</div>
                              )}
                              {inspection.diseases && inspection.diseases.length > 0 && (
                                <div className="text-red-600 font-medium text-sm mt-1">🦠 Diseases: {inspection.diseases.join(', ')}</div>
                              )}
                              {inspection.pests && inspection.pests.length > 0 && (
                                <div className="text-orange-600 font-medium text-sm mt-1">🐛 Pests: {inspection.pests.join(', ')}</div>
                              )}
                              {inspection.mite_percentage && (
                                <div className="text-amber-600 font-medium text-sm mt-1">🔬 Mites: {inspection.mite_percentage}%</div>
                              )}
                              {inspection.nectar_level === 'Low' && (
                                <div className="text-yellow-600 font-medium text-sm mt-1">🍯 Nectar: Low</div>
                              )}
                              {inspection.pollen_level === 'Low' && (
                                <div className="text-yellow-600 font-medium text-sm mt-1">🌼 Pollen: Low</div>
                              )}
                              {inspection.honey_brood_level === 'Low' && (
                                <div className="text-yellow-600 font-medium text-sm mt-1">🍯 Honey: Low</div>
                              )}
                              {inspection.hive_weight && (
                                <div className="text-emerald-600 font-medium text-sm mt-1">⚖️ Weight: {
                                  weightSettings.unit_preference === 'kg' 
                                    ? `${parseFloat(inspection.hive_weight).toFixed(1)} kg`
                                    : `${(parseFloat(inspection.hive_weight) * 2.20462).toFixed(1)} lbs`
                                }</div>
                              )}
                            </div>
                            <Eye className="h-5 w-5 text-stone-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </div>{/* end hive-overview-content */}

      <EditHiveDialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        hive={hive}
        yards={yards}
        onUpdate={handleUpdateHive}
        allHives={allHives}
      />

      <QRCodeGenerator
        open={showQRDialog}
        onClose={() => setShowQRDialog(false)}
        type="hive"
        id={hive?.id}
        name={`Hive ${hive?.hive_number}`}
        yardName={hive?.yard}
        yardLocation={(() => {
          const yd = yards.find(y => y.name === hive?.yard || y.name?.trim() === hive?.yard);
          return yd?.location || hive?.location || '';
        })()}
      />

      <SplitWizard
        open={showSplitWizard}
        onOpenChange={setShowSplitWizard}
        hive={hive}
        hives={allHives}
        yards={yards}
        onSuccess={fetchData}
      />

      <MiteCountDialog
        open={showMiteCountDialog}
        onClose={() => setShowMiteCountDialog(false)}
        hives={allHives}
        token={token}
        preselectedHiveId={hiveId}
        onSuccess={fetchData}
      />
    </div>
  );
};

// EditHiveDialog is now imported from ../components/EditHiveDialog

export default HiveDetail;
